import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Bug,
  Cable,
  Camera,
  CheckSquare,
  Cpu,
  DatabaseBackup,
  Globe,
  HardDrive,
  KeyRound,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Lock,
  Monitor,
  Network,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  Printer,
  Router,
  Scan,
  Server,
  Share2,
  Signal,
  Split,
  Square,
  Shield,
  Trash2,
  Users,
  Video,
  Wallet,
  Waypoints,
  Wifi,
  Wand2,
  ZoomIn,
  ZoomOut,
  type LucideIcon,
} from 'lucide-react';
import type { Customer, HealthState, Resource, VpnLinkView } from '@itr/shared';
import { HEALTH_SEVERITY, accessHostFor, formatIpv4WithMask, isMikrotik, rollUpHealth, rollupMembers } from '@itr/shared';
import { IgnoreBulkButtons, IgnoredBadge, ResourceHealth } from './ResourceHealth';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Health, ReachDot, SelectionBar, bulkResultMessage, runBulk, useFetch, useSelection, useToast } from './ui';
import { DeviceFilterBar, useDeviceFilter, useResourceDescriber } from './DeviceFilter';
import type { SiteRow } from '../pages/Clients';
import { IpMask, VpnLinksTable, nodeAddressMap, runVpnDiscovery, type NodeAddress } from './VpnLinks';

interface ResourceKindMeta {
  kind: string;
  icon: string;
}

/** Present only while the map is selectable; `null` means nodes open on click. */
interface NodeSelection {
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** True in select mode, where a plain click toggles instead of opening. */
  active: boolean;
}

/** Every icon name a plugin's `resourceKinds[].icon` can currently name, so a
 *  network-map node shows the same symbol as the rest of the app rather than
 *  a generic dot. Falls back to `Box` for anything not in this list — new
 *  plugins default to `'box'` server-side too, so that's never a crash. */
const KIND_ICONS: Record<string, LucideIcon> = {
  bug: Bug,
  cable: Cable,
  camera: Camera,
  cpu: Cpu,
  'database-backup': DatabaseBackup,
  globe: Globe,
  'hard-drive': HardDrive,
  'key-round': KeyRound,
  layers: Layers,
  'layout-dashboard': LayoutDashboard,
  'life-buoy': LifeBuoy,
  lock: Lock,
  monitor: Monitor,
  network: Network,
  phone: Phone,
  'phone-call': PhoneCall,
  'phone-outgoing': PhoneOutgoing,
  printer: Printer,
  router: Router,
  scan: Scan,
  server: Server,
  'share-2': Share2,
  signal: Signal,
  split: Split,
  shield: Shield,
  users: Users,
  video: Video,
  wallet: Wallet,
  wifi: Wifi,
};

/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */

const NODE_W = 210;
const H_GAP = 18;
const V_GAP = 56;
const PAD = 24;
const DEFAULT_H = 84;
/** Pixels the pointer must travel before a press becomes a drag rather than a click. */
const DRAG_THRESHOLD = 4;
/** Room under the lowest node for VPN curves, which dip below their ends. */
const VPN_ROOM = 100;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2.5;
const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

type Point = { x: number; y: number };

/** Horizontal spreads the tree across the page, parents above children; vertical runs it left to right. */
type ArrangeMode = 'horizontal' | 'vertical';

/** What the map puts on every node element: position, measuring ref, drag handlers and state attributes. */
type NodeProps = React.HTMLAttributes<HTMLDivElement> &
  React.RefAttributes<HTMLDivElement> & { [attr: `data-${string}`]: string | boolean | undefined };

type TreeNode =
  | { id: 'root'; type: 'root'; children: TreeNode[] }
  | { id: string; type: 'site'; site: SiteRow; children: TreeNode[] }
  | { id: 'unassigned'; type: 'group'; children: TreeNode[] }
  | { id: string; type: 'resource'; resource: Resource; children: TreeNode[] };

/**
 * Tidy tree layout: every leaf gets its own column, a parent sits centred over
 * the span of its children, and each depth is one row as tall as its tallest
 * node. Positions are node top-left corners in layout coordinates.
 */
function autoLayout(root: TreeNode, heightOf: (id: string) => number, mode: ArrangeMode = 'vertical'): Map<string, Point> {
  if (mode === 'vertical') return verticalLayout(root, heightOf);
  const rowHeights: number[] = [];
  const measure = (n: TreeNode, depth: number) => {
    rowHeights[depth] = Math.max(rowHeights[depth] ?? 0, heightOf(n.id));
    n.children.forEach((c) => measure(c, depth + 1));
  };
  measure(root, 0);
  const rowY: number[] = [];
  rowHeights.reduce((y, h, depth) => {
    rowY[depth] = y;
    return y + h + V_GAP;
  }, 0);

  const positions = new Map<string, Point>();
  let cursor = 0;
  const place = (n: TreeNode, depth: number): number => {
    let centre: number;
    if (n.children.length === 0) {
      centre = cursor + NODE_W / 2;
      cursor += NODE_W + H_GAP;
    } else {
      const centres = n.children.map((c) => place(c, depth + 1));
      centre = (centres[0]! + centres[centres.length - 1]!) / 2;
    }
    positions.set(n.id, { x: centre - NODE_W / 2, y: rowY[depth]! });
    return centre;
  };
  place(root, 0);
  return positions;
}

/**
 * The same tree turned on its side: the root at the left, each depth one
 * column, every leaf its own row, and a parent centred beside its children.
 */
function verticalLayout(root: TreeNode, heightOf: (id: string) => number): Map<string, Point> {
  const positions = new Map<string, Point>();
  let cursor = 0;
  const place = (n: TreeNode, depth: number): number => {
    const h = heightOf(n.id);
    let centre: number;
    if (n.children.length === 0) {
      centre = cursor + h / 2;
      cursor += h + H_GAP;
    } else {
      const centres = n.children.map((c) => place(c, depth + 1));
      centre = (centres[0]! + centres[centres.length - 1]!) / 2;
    }
    positions.set(n.id, { x: depth * (NODE_W + V_GAP), y: centre - h / 2 });
    return centre;
  };
  place(root, 0);
  return positions;
}

function walk(n: TreeNode, visit: (n: TreeNode, parent: TreeNode | null) => void, parent: TreeNode | null = null): void {
  visit(n, parent);
  n.children.forEach((c) => walk(c, visit, n));
}

/** Elbow from a parent's right edge to a child's left edge, for the vertical arrangement. */
function sideEdgePath(from: Point, to: Point): string {
  if (to.x - from.x > 16) {
    const mid = from.x + Math.min(V_GAP / 2, (to.x - from.x) / 2);
    return `M ${from.x} ${from.y} H ${mid} V ${to.y} H ${to.x}`;
  }
  const bend = Math.max(40, Math.abs(to.x - from.x) / 2);
  return `M ${from.x} ${from.y} C ${from.x + bend} ${from.y}, ${to.x - bend} ${to.y}, ${to.x} ${to.y}`;
}

/** Orthogonal elbow from a parent's bottom to a child's top — curved instead when the child has been moved above it. */
function edgePath(from: Point, to: Point): string {
  if (to.y - from.y > 16) {
    const mid = from.y + Math.min(V_GAP / 2, (to.y - from.y) / 2);
    return `M ${from.x} ${from.y} V ${mid} H ${to.x} V ${to.y}`;
  }
  const bend = Math.max(40, Math.abs(to.y - from.y) / 2);
  return `M ${from.x} ${from.y} C ${from.x} ${from.y + bend}, ${to.x} ${to.y - bend}, ${to.x} ${to.y}`;
}

/**
 * Visual map of a client's sites and resources: sites branch off the client,
 * resources branch off their site (or a dashed "Unassigned" group when no
 * site claims them yet), and a resource with a parent (a switch under its
 * router, a device a MikroTik discovered) nests under that parent instead of
 * sitting as its site's sibling. Every node but the root links to its own
 * page.
 *
 * Laid out automatically, but any node can be dragged somewhere else; the
 * position is saved for everyone who opens this map (`layoutScope`), and
 * "Horizontal"/"Vertical" arrange discard the saved positions. Dropping a resource onto another
 * resource still re-parents it.
 */
export function NetworkDiagram({
  client,
  sites,
  resources: allResources,
  layoutScope,
  onChanged,
}: {
  client: Customer;
  sites: SiteRow[];
  resources: Resource[];
  /** Which saved arrangement this map uses: `client:<id>` or `site:<id>`. */
  layoutScope: string;
  /** Called after a drag-and-drop reparent or a delete succeeds, so the caller can refetch. */
  onChanged?: () => void;
}) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const toast = useToast();
  const { data: kinds } = useFetch(() => api.get<ResourceKindMeta[]>('/api/resource-kinds'), []);
  const iconByKind = useMemo(() => new Map((kinds ?? []).map((k) => [k.kind, k.icon])), [kinds]);

  const canEdit = can('resource:write');

  // The map draws only what passes the filter (type, status, name, IP, MAC
  // prefix); health rollups and the VPN discovery button still cover every
  // resource, so hiding a device never changes what a site reports.
  const describe = useResourceDescriber();
  const filter = useDeviceFilter(allResources, describe);
  const resources = filter.visible;

  // Select mode turns a click into a toggle; Ctrl/Cmd-click toggles without
  // entering it, the way a file manager does.
  const [selectMode, setSelectMode] = useState(false);
  const resourceIds = useMemo(() => resources.map((r) => r.id), [resources]);
  const selection = useSelection(resourceIds);
  const { count: selectedCount, clear: clearSelection } = selection;
  useEffect(() => {
    if (!selectMode && selectedCount === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      clearSelection();
      setSelectMode(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectMode, selectedCount, clearSelection]);

  const nodeSelection: NodeSelection | undefined = canEdit
    ? { isSelected: selection.isSelected, toggle: selection.toggle, active: selectMode }
    : undefined;

  const bulkDelete = async () => {
    const ids = selection.selectedIds;
    const orphans = resources.filter((r) => r.parentId && ids.includes(r.parentId) && !ids.includes(r.id)).length;
    const note = orphans ? ` ${orphans} device(s) under them stay, moved up to their site.` : '';
    if (!confirm(`Delete ${ids.length} resource(s)? Their history and metrics go with them.${note}`)) return;
    const result = await runBulk(ids, (id) => api.delete(`/api/resources/${id}`));
    const { message, intent } = bulkResultMessage('Deleted', result);
    toast(message, intent);
    selection.clear();
    setSelectMode(false);
    onChanged?.();
  };

  const { bySite, loose, childrenByParent } = useMemo(() => {
    // A resource whose parent isn't in this same resource set (filtered out,
    // or genuinely elsewhere) renders as top-level rather than vanishing —
    // losing a node because its parent happens to be out of scope would be
    // worse than showing it one level too high.
    const ids = new Set(resources.map((r) => r.id));
    const childrenByParent = new Map<string, Resource[]>();
    for (const r of resources) {
      if (r.parentId && ids.has(r.parentId)) {
        childrenByParent.set(r.parentId, [...(childrenByParent.get(r.parentId) ?? []), r]);
      }
    }

    const topLevel = resources.filter((r) => !r.parentId || !ids.has(r.parentId));
    const bySite = new Map<string, Resource[]>();
    const loose: Resource[] = [];
    for (const r of topLevel) {
      if (r.siteId) bySite.set(r.siteId, [...(bySite.get(r.siteId) ?? []), r]);
      else loose.push(r);
    }
    return { bySite, loose, childrenByParent };
  }, [resources]);

  const resourceById = useMemo(() => new Map(resources.map((r) => [r.id, r])), [resources]);
  const clientRollup = useMemo(() => rollupMembers(allResources), [allResources]);
  const clientHealth = rollUpHealth(clientRollup.map((r) => r.health));

  const tree = useMemo<TreeNode>(() => {
    // `seen` guards against a corrupt parent chain forming a cycle, which
    // would otherwise recurse forever and hang the tab.
    const seen = new Set<string>();
    const branch = (r: Resource): TreeNode | null => {
      if (seen.has(r.id)) return null;
      seen.add(r.id);
      const children = (childrenByParent.get(r.id) ?? []).map(branch).filter((c): c is TreeNode => c !== null);
      return { id: r.id, type: 'resource', resource: r, children };
    };
    const top: TreeNode[] = sites.map((site) => ({
      id: `site:${site.id}`,
      type: 'site' as const,
      site,
      children: (bySite.get(site.id) ?? []).map(branch).filter((c): c is TreeNode => c !== null),
    }));
    if (loose.length > 0) {
      top.push({ id: 'unassigned', type: 'group', children: loose.map(branch).filter((c): c is TreeNode => c !== null) });
    }
    return { id: 'root', type: 'root', children: top };
  }, [sites, bySite, loose, childrenByParent]);

  // VPN links come from their own endpoint because the far end is usually
  // outside this client (the provider's hub). Kept to links with at least
  // one end on this map, so a site page shows only its own.
  const vpn = useFetch(() => api.get<VpnLinkView[]>('/api/vpn-links', { customerId: client.id }), [client.id], { refreshMs: 60_000 });
  const vpnLinks = useMemo(
    () => (vpn.data ?? []).filter((l) => [l.a.resourceId, l.b.resourceId].some((id) => id && resourceById.has(id))),
    [vpn.data, resourceById],
  );
  const addressesById = useMemo(() => nodeAddressMap(resources, vpnLinks), [resources, vpnLinks]);
  const [showVpn, setShowVpn] = useState(true);

  const mikrotikIds = useMemo(() => allResources.filter(isMikrotik).map((r) => r.id), [allResources]);
  const [discovering, setDiscovering] = useState(false);
  const discoverVpn = async () => {
    setDiscovering(true);
    try {
      const result = await runBulk(mikrotikIds, runVpnDiscovery);
      const { message, intent } = bulkResultMessage('Read VPN details from', result);
      toast(`${message} router${result.ok + result.failed === 1 ? '' : 's'}`, intent);
      vpn.reload();
      onChanged?.();
    } finally {
      setDiscovering(false);
    }
  };

  /* ---- Node sizes: measured, so the layout leaves room for tall nodes ---- */

  const [heights, setHeights] = useState<Record<string, number>>({});
  const observer = useRef<ResizeObserver | null>(null);
  const observed = useRef(new Map<string, HTMLElement>());
  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      setHeights((prev) => {
        let next = prev;
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const id = el.dataset.ndId;
          const h = Math.round(el.offsetHeight);
          if (id && prev[id] !== h) {
            if (next === prev) next = { ...prev };
            next[id] = h;
          }
        }
        return next;
      });
    });
    observer.current = ro;
    observed.current.forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, []);
  // One stable ref callback per node id, so React doesn't detach and
  // re-attach every node's observer on each render.
  const refCallbacks = useRef(new Map<string, (el: HTMLElement | null) => void>());
  const nodeRef = useCallback((id: string) => {
    let cb = refCallbacks.current.get(id);
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        const previous = observed.current.get(id);
        if (previous) observer.current?.unobserve(previous);
        if (el) {
          observed.current.set(id, el);
          observer.current?.observe(el);
        } else observed.current.delete(id);
      };
      refCallbacks.current.set(id, cb);
    }
    return cb;
  }, []);
  const heightOf = useCallback((id: string) => heights[id] ?? DEFAULT_H, [heights]);

  const modeKey = `itr.netmap.mode:${layoutScope}`;
  const [mode, setMode] = useState<ArrangeMode>(() => {
    try {
      return localStorage.getItem(modeKey) === 'horizontal' ? 'horizontal' : 'vertical';
    } catch {
      return 'vertical';
    }
  });
  const auto = useMemo(() => autoLayout(tree, heightOf, mode), [tree, heightOf, mode]);

  /* ---- Saved positions ---- */

  const layout = useFetch(
    () => api.get<{ positions: Record<string, Point> }>(`/api/diagram-layouts/${encodeURIComponent(layoutScope)}`),
    [layoutScope],
  );
  const [saved, setSaved] = useState<Record<string, Point>>({});
  useEffect(() => {
    if (layout.data) setSaved(layout.data.positions);
  }, [layout.data]);

  const persist = async (updates: Record<string, Point | null>) => {
    setSaved((prev) => {
      const next = { ...prev };
      for (const [id, pos] of Object.entries(updates)) {
        if (pos) next[id] = pos;
        else delete next[id];
      }
      return next;
    });
    try {
      await api.patch(`/api/diagram-layouts/${encodeURIComponent(layoutScope)}`, { positions: updates });
    } catch (err) {
      toast('Could not save the map layout', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const rearrange = async (next: ArrangeMode) => {
    if (
      Object.keys(saved).length > 0 &&
      !confirm('Put every node back in its automatic position? The arrangement saved for this map is discarded for everyone.')
    )
      return;
    try {
      await api.delete(`/api/diagram-layouts/${encodeURIComponent(layoutScope)}`);
      setSaved({});
      setMode(next);
      try {
        localStorage.setItem(modeKey, next);
      } catch {
        // The arrangement just isn't remembered for next visit.
      }
      toast('Map rearranged', 'good');
    } catch (err) {
      toast('Could not rearrange the map', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  /* ---- Zoom ---- */

  const isEmpty = sites.length === 0 && allResources.length === 0;

  // Per viewer and per map: how far someone zoomed is their own business,
  // not part of the arrangement saved for everyone.
  const zoomKey = `itr.netmap.zoom:${layoutScope}`;
  const [zoom, setZoomState] = useState(() => {
    try {
      const stored = Number(localStorage.getItem(zoomKey));
      return stored ? clampZoom(stored) : 1;
    } catch {
      return 1;
    }
  });
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const scrollRef = useRef<HTMLDivElement>(null);
  const sizerRef = useRef<HTMLDivElement>(null);
  /** The map point that must stay under the cursor across a wheel zoom, applied once the new size has rendered. */
  const zoomAnchor = useRef<{ mapX: number; mapY: number; clientX: number; clientY: number } | null>(null);

  const setZoom = useCallback(
    (next: number) => {
      const z = clampZoom(Math.round(next * 100) / 100);
      setZoomState(z);
      try {
        localStorage.setItem(zoomKey, String(z));
      } catch {
        // Private window or blocked storage — the zoom just isn't remembered.
      }
    },
    [zoomKey],
  );

  // React's onWheel listener is passive, so it cannot stop the page scrolling
  // underneath; this one is registered by hand for that reason.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const sizer = sizerRef.current;
      // Plain scrolling scrolls the page; zoom needs Ctrl/Cmd (a trackpad pinch sends ctrlKey too).
      if (!sizer || e.deltaY === 0 || !(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const current = zoomRef.current;
      // Lines (Firefox) and pages are far coarser than pixels; normalise so a
      // mouse notch is ~15% and a trackpad pinch (ctrlKey) stays smooth.
      const px = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1);
      const next = clampZoom(current * Math.exp(-px * 0.0015));
      if (next === current) return;
      const rect = sizer.getBoundingClientRect();
      zoomAnchor.current = {
        mapX: (e.clientX - rect.left) / current,
        mapY: (e.clientY - rect.top) / current,
        clientX: e.clientX,
        clientY: e.clientY,
      };
      setZoom(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // `isEmpty`: the map renders nothing (so no scroll box) until it has content.
  }, [setZoom, isEmpty]);

  useLayoutEffect(() => {
    const anchor = zoomAnchor.current;
    const el = scrollRef.current;
    const sizer = sizerRef.current;
    zoomAnchor.current = null;
    if (!anchor || !el || !sizer) return;
    const rect = sizer.getBoundingClientRect();
    el.scrollLeft += rect.left + anchor.mapX * zoom - anchor.clientX;
    el.scrollTop += rect.top + anchor.mapY * zoom - anchor.clientY;
  }, [zoom]);

  /* ---- Moving and re-parenting ---- */

  const parentOf = useMemo(() => {
    const map = new Map<string, string>();
    walk(tree, (n, parent) => {
      if (parent) map.set(n.id, parent.id);
    });
    return map;
  }, [tree]);
  const nodeById = useMemo(() => {
    const map = new Map<string, TreeNode>();
    walk(tree, (n) => map.set(n.id, n));
    return map;
  }, [tree]);
  const subtreeIds = (id: string): string[] => {
    const out: string[] = [];
    const start = nodeById.get(id);
    if (start) walk(start, (n) => out.push(n.id));
    return out;
  };

  const [drag, setDrag] = useState<{ ids: string[]; dx: number; dy: number; overId: string | null } | null>(null);
  const press = useRef<{ id: string; ids: string[]; startX: number; startY: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);

  const positionOf = (id: string): Point => {
    const base = saved[id] ?? auto.get(id) ?? { x: 0, y: 0 };
    return drag?.ids.includes(id) ? { x: base.x + drag.dx, y: base.y + drag.dy } : base;
  };

  // True when `candidateId` is `ancestorId` itself or nests somewhere under
  // it — dropping a node onto its own descendant would create a cycle.
  const isSelfOrDescendant = (ancestorId: string, candidateId: string) => subtreeIds(ancestorId).includes(candidateId);

  /**
   * What a dragged resource may be dropped onto to re-parent it: another
   * resource that isn't under it, or its own site's node — "un-nest back to
   * the site root", not a move between sites/clients, which would also need
   * a siteId/customerId change this gesture doesn't make.
   */
  const dropTargetValid = (draggedId: string, targetId: string): boolean => {
    const dragged = resourceById.get(draggedId);
    if (!dragged) return false;
    if (targetId.startsWith('site:')) return dragged.siteId === targetId.slice(5) && Boolean(dragged.parentId);
    return resourceById.has(targetId) && !isSelfOrDescendant(draggedId, targetId) && dragged.parentId !== targetId;
  };

  const reparent = async (resourceId: string, parentId: string | null) => {
    try {
      await api.patch(`/api/resources/${resourceId}`, { parentId });
      // Let it settle under its new parent rather than keep a spot chosen for the old one.
      await persist({ [resourceId]: null });
      toast('Resource moved', 'good');
      onChanged?.();
    } catch (err) {
      toast('Could not move the resource', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const canArrange = canEdit && !selectMode;

  const onPointerDown = (id: string) => (e: React.PointerEvent) => {
    if (!canArrange || e.button !== 0 || e.ctrlKey || e.metaKey) return;
    press.current = { id, ids: e.shiftKey ? subtreeIds(id) : [id], startX: e.clientX, startY: e.clientY, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const p = press.current;
    if (!p) return;
    const dx = (e.clientX - p.startX) / zoom;
    const dy = (e.clientY - p.startY) / zoom;
    if (!p.moved && Math.hypot(e.clientX - p.startX, e.clientY - p.startY) < DRAG_THRESHOLD) return;
    p.moved = true;
    let overId: string | null = null;
    if (resourceById.has(p.id)) {
      for (const el of document.elementsFromPoint(e.clientX, e.clientY)) {
        const target = (el as HTMLElement).closest<HTMLElement>('[data-nd-drop]')?.dataset.ndDrop;
        if (target && !p.ids.includes(target)) {
          overId = dropTargetValid(p.id, target) ? target : null;
          break;
        }
      }
    }
    setDrag({ ids: p.ids, dx, dy, overId });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const p = press.current;
    press.current = null;
    if (!p?.moved) return;
    suppressClick.current = true;
    const current = drag;
    setDrag(null);
    if (!current) return;
    if (current.overId) {
      void reparent(p.id, current.overId.startsWith('site:') ? null : current.overId);
      return;
    }
    const updates: Record<string, Point> = {};
    for (const id of current.ids) {
      const base = saved[id] ?? auto.get(id) ?? { x: 0, y: 0 };
      updates[id] = { x: base.x + (e.clientX - p.startX) / zoom, y: base.y + (e.clientY - p.startY) / zoom };
    }
    void persist(updates);
  };

  const onPointerCancel = () => {
    press.current = null;
    setDrag(null);
  };

  const pan = useRef<{ startX: number; startY: number; left: number; top: number } | null>(null);
  const [panning, setPanning] = useState(false);
  const onPanDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || e.button !== 0 || (e.target as HTMLElement).closest('.nd-node, a, button')) return;
    pan.current = { startX: e.clientX, startY: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    setPanning(true);
    el.setPointerCapture(e.pointerId);
  };
  const onPanMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = pan.current;
    const el = scrollRef.current;
    if (!p || !el) return;
    el.scrollLeft = p.left - (e.clientX - p.startX);
    el.scrollTop = p.top - (e.clientY - p.startY);
  };
  const onPanEnd = () => {
    pan.current = null;
    setPanning(false);
  };

  /** Swallows the click that follows a drag, so dropping a node doesn't also open it. */
  const clickGuard = (open: () => void) => () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    open();
  };

  /* ---- Geometry ---- */

  const nodes: TreeNode[] = [];
  walk(tree, (n) => nodes.push(n));

  // Bounds exclude the node being dragged, so the rest of the map doesn't
  // shift under the pointer while it moves; everything settles on drop.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    const pos = saved[n.id] ?? auto.get(n.id) ?? { x: 0, y: 0 };
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
    maxX = Math.max(maxX, pos.x + NODE_W);
    maxY = Math.max(maxY, pos.y + heightOf(n.id));
  }
  const ox = PAD - minX;
  const oy = PAD - minY;
  const vpnDrawable = showVpn && vpnLinks.some((l) => l.a.resourceId && l.b.resourceId && nodeById.has(l.a.resourceId) && nodeById.has(l.b.resourceId));
  const width = maxX - minX + PAD * 2;
  const height = maxY - minY + PAD * 2 + (vpnDrawable ? VPN_ROOM : 0);
  const box = (id: string) => {
    const pos = positionOf(id);
    return { x: pos.x + ox, y: pos.y + oy, w: NODE_W, h: heightOf(id) };
  };

  if (isEmpty) return null;


  const nodeProps = (id: string, dropId?: string): NodeProps => {
    const b = box(id);
    return {
      ref: nodeRef(id),
      'data-nd-id': id,
      'data-nd-drop': dropId,
      'data-moving': (drag?.ids.includes(id) && drag.ids[0] === id) || undefined,
      'data-drag-over': (drag?.overId && drag.overId === (dropId ?? id)) || undefined,
      style: { left: b.x, top: b.y },
      onPointerDown: onPointerDown(id),
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    };
  };

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Network size={14} />
        <span className="card-title">Network map</span>
        <span className="grow" />
        <span className="subtle" style={{ fontSize: 12 }}>
          {sites.length} site{sites.length === 1 ? '' : 's'} · {filter.active ? `${resources.length} of ${allResources.length}` : resources.length} resource
          {allResources.length === 1 ? '' : 's'}
          {vpnLinks.length > 0 && ` · ${vpnLinks.length} VPN link${vpnLinks.length === 1 ? '' : 's'}`}
        </span>
        <span className="nd-zoom-controls">
          <button className="btn btn-sm" onClick={() => setZoom(zoom / 1.2)} disabled={zoom <= MIN_ZOOM} title="Zoom out" aria-label="Zoom out">
            <ZoomOut size={12} />
          </button>
          <button className="btn btn-sm" onClick={() => setZoom(1)} title="Reset zoom to 100% (or hold Ctrl/Cmd and scroll over the map to zoom)">
            {Math.round(zoom * 100)}%
          </button>
          <button className="btn btn-sm" onClick={() => setZoom(zoom * 1.2)} disabled={zoom >= MAX_ZOOM} title="Zoom in" aria-label="Zoom in">
            <ZoomIn size={12} />
          </button>
        </span>
        {vpnLinks.length > 0 && (
          <button
            className={`btn btn-sm${showVpn ? ' btn-primary' : ''}`}
            onClick={() => setShowVpn(!showVpn)}
            title="Show or hide VPN connections on the map"
            aria-pressed={showVpn}
          >
            <Waypoints size={12} /> VPN
          </button>
        )}
        {can('tools:run') && mikrotikIds.length > 0 && (
          <button
            className="btn btn-sm"
            onClick={discoverVpn}
            disabled={discovering}
            title="Read addresses, PPP active sessions and tunnel routes from this client's MikroTiks over SSH"
          >
            {discovering ? <span className="spinner" style={{ width: 12, height: 12 }} /> : <Waypoints size={12} />}
            {discovering ? 'Discovering…' : 'Discover VPN'}
          </button>
        )}
        {canEdit && (
          <>
            <button
              className="btn btn-sm"
              onClick={() => rearrange('horizontal')}
              title="Discard the saved arrangement and lay the map out automatically, spreading across the page"
            >
              <Wand2 size={12} /> Horizontal arrange
            </button>
            <button
              className="btn btn-sm"
              onClick={() => rearrange('vertical')}
              title="Discard the saved arrangement and lay the map out automatically, running left to right"
            >
              <Wand2 size={12} /> Vertical arrange
            </button>
          </>
        )}
        {canEdit && resources.length > 0 && (
          <button
            className={`btn btn-sm${selectMode ? ' btn-primary' : ''}`}
            onClick={() => {
              if (selectMode) selection.clear();
              setSelectMode(!selectMode);
            }}
            title="Pick several resources to ignore, unignore or delete at once (or Ctrl/Cmd-click a node)"
          >
            <CheckSquare size={12} /> {selectMode ? 'Done' : 'Select'}
          </button>
        )}
      </div>
      {allResources.length > 0 && <DeviceFilterBar filter={filter} total={allResources.length} />}
      {filter.active && resources.length === 0 && (
        <div className="subtle" style={{ padding: '8px var(--itr-pad) 0', fontSize: 12.5 }}>
          No resources match the filter.
        </div>
      )}
      {canEdit && (
        <div style={{ padding: '10px var(--itr-pad) 0' }}>
          <SelectionBar selection={selection}>
            <button className="btn btn-sm" onClick={selection.toggleAll}>
              {selection.allSelected ? 'Select none' : 'Select all'}
            </button>
            <IgnoreBulkButtons selection={selection} onChanged={onChanged} />
            <button className="btn btn-sm btn-danger" onClick={bulkDelete}>
              <Trash2 size={12} /> Delete
            </button>
          </SelectionBar>
        </div>
      )}
      {canArrange && (
        <div className="subtle" style={{ padding: '8px var(--itr-pad) 0', fontSize: 11.5 }}>
          Drag a node to move it — its place is saved for this map. Shift-drag moves it with everything under it; drop a device onto
          another to put it under that one. Drag empty space to pan; hold Ctrl/Cmd and scroll to zoom.
        </div>
      )}
      <div
        className="netdiagram-scroll"
        ref={scrollRef}
        style={{ cursor: panning ? 'grabbing' : 'grab' }}
        onPointerDown={onPanDown}
        onPointerMove={onPanMove}
        onPointerUp={onPanEnd}
        onPointerCancel={onPanEnd}
      >
        {/* Takes up the zoomed size, so the scrollbars match what is drawn;
            the canvas inside keeps working in unscaled map coordinates. */}
        <div className="nd-zoom" ref={sizerRef} style={{ width: width * zoom, height: height * zoom }}>
        <div className="nd-canvas" data-arrange={canArrange || undefined} style={{ width, height, transform: `scale(${zoom})` }}>
          <svg className="nd-edges" width={width} height={height} aria-hidden="true">
            {nodes.map((n) => {
              const parentId = parentOf.get(n.id);
              if (!parentId) return null;
              const p = box(parentId);
              const c = box(n.id);
              const d =
                mode === 'vertical'
                  ? sideEdgePath({ x: p.x + p.w, y: p.y + p.h / 2 }, { x: c.x, y: c.y + c.h / 2 })
                  : edgePath({ x: p.x + p.w / 2, y: p.y + p.h }, { x: c.x + c.w / 2, y: c.y });
              return <path key={n.id} d={d} />;
            })}
          </svg>
          {vpnDrawable && <VpnLayer links={vpnLinks} box={(id) => (nodeById.has(id) ? box(id) : null)} width={width} height={height} />}

          {nodes.map((n) => {
            if (n.type === 'root') {
              return (
                <div key={n.id} className="nd-node nd-node-root" data-health={clientHealth} {...nodeProps(n.id)}>
                  <span className="nd-node-title">{client.name}</span>
                  <span className="nd-node-sub">{client.code}</span>
                  {/* Same rollup as a site's, across the whole client: its worst
                      resource, flagged ones excluded, offenders in the tooltip. */}
                  <Health
                    health={clientRollup.length ? clientHealth : 'unknown'}
                    reason={clientRollup.length ? (rollUpReason(clientRollup) ?? `All ${clientRollup.length} resources up`) : null}
                  />
                </div>
              );
            }
            if (n.type === 'group') {
              return (
                <div key={n.id} className="nd-node nd-node-group" {...nodeProps(n.id)}>
                  Unassigned
                </div>
              );
            }
            if (n.type === 'site') {
              const site = n.site;
              // Rolled up across every resource at this site, descendants
              // included — a router shown "up" while its discovered child
              // is down would misreport the site as healthy. Resources
              // flagged to ignore rollup are left out on purpose.
              const allSiteResources = rollupMembers(allResources.filter((r) => r.siteId === site.id));
              const health = rollUpHealth(allSiteResources.map((r) => r.health));
              const open = () => navigate(`/clients/${client.id}/sites/${site.id}`);
              return (
                <div
                  key={n.id}
                  className="nd-node nd-node-site"
                  data-clickable="true"
                  data-health={health}
                  role="button"
                  tabIndex={0}
                  onClick={clickGuard(open)}
                  onKeyDown={(e) => e.key === 'Enter' && open()}
                  {...nodeProps(n.id, n.id)}
                >
                  <span className="nd-node-title">{site.name}</span>
                  {site.address && <span className="nd-node-sub">{site.address}</span>}
                  <Health
                    health={allSiteResources.length ? health : 'unknown'}
                    reason={allSiteResources.length ? rollUpReason(allSiteResources) : null}
                  />
                </div>
              );
            }
            return (
              <ResourceNode
                key={n.id}
                resource={n.resource}
                icon={iconByKind.get(n.resource.kind)}
                addresses={addressesById.get(n.id) ?? []}
                onOpen={clickGuard(() => navigate(`/resources/${n.id}`))}
                selection={nodeSelection}
                nodeProps={nodeProps(n.id, n.id)}
              />
            );
          })}
        </div>
        </div>
      </div>
      {showVpn && vpnLinks.length > 0 && (
        <div style={{ borderTop: 'var(--itr-border-w) solid var(--itr-border)' }}>
          <VpnLinksTable links={vpnLinks} />
        </div>
      )}
    </div>
  );
}

const LINK_COLOR: Partial<Record<HealthState, string>> = {
  down: 'var(--itr-critical)',
  degraded: 'var(--itr-warning)',
};

/**
 * VPN links as dashed curves between the two ends' nodes, dipping below both
 * so they read as "across" rather than as another parent/child line. A link
 * with only one end on the map has nothing to draw to — it is listed in the
 * table below instead.
 */
function VpnLayer({
  links,
  box,
  width,
  height,
}: {
  links: VpnLinkView[];
  box: (id: string) => { x: number; y: number; w: number; h: number } | null;
  width: number;
  height: number;
}) {
  const drawn = links.flatMap((link) => {
    const a = link.a.resourceId ? box(link.a.resourceId) : null;
    const b = link.b.resourceId ? box(link.b.resourceId) : null;
    if (!a || !b || link.a.resourceId === link.b.resourceId) return [];
    const x0 = a.x + a.w / 2;
    const y0 = a.y + a.h;
    const x1 = b.x + b.w / 2;
    const y1 = b.y + b.h;
    const dip = Math.max(y0, y1) + 34 + Math.min(80, Math.abs(x1 - x0) * 0.08);
    // The tunnel's own checked state when the VPN watch has one; otherwise
    // the worse of its two devices, as the only hint available.
    const tunnel = link.status?.state;
    const worst =
      tunnel === 'up' || tunnel === 'down'
        ? tunnel
        : [link.a.health, link.b.health].includes('down')
          ? 'down'
          : [link.a.health, link.b.health].includes('degraded')
            ? 'degraded'
            : 'up';
    return [
      {
        link,
        d: `M ${x0} ${y0} C ${x0} ${dip}, ${x1} ${dip}, ${x1} ${y1}`,
        // Midpoint of the cubic: (P0 + 3·P1 + 3·P2 + P3) / 8.
        mid: { x: (x0 + x1) / 2, y: (y0 + y1 + 6 * dip) / 8 },
        color: LINK_COLOR[worst as HealthState] ?? 'var(--itr-primary)',
      },
    ];
  });
  if (drawn.length === 0) return null;

  return (
    <>
      <svg className="nd-vpn-svg" width={width} height={height} aria-hidden="true">
        {drawn.map(({ link, d, color }) => (
          <path key={link.id} d={d} fill="none" stroke={color} strokeWidth={1.6} strokeDasharray="5 4" />
        ))}
      </svg>
      {drawn.map(({ link, mid, color }) => (
        <div
          key={link.id}
          className="nd-vpn-label"
          style={{ left: mid.x, top: mid.y, borderColor: color }}
          title={`${link.a.name} ${link.a.address ? `${link.a.address.ip}/${link.a.address.prefix ?? '?'}` : ''} ⇄ ${link.b.name} ${
            link.b.address ? `${link.b.address.ip}/${link.b.address.prefix ?? '?'}` : ''
          }${link.routes?.length ? `\nroutes ${link.routes.join(', ')}` : ''}${link.status ? `\ntunnel ${link.status.state}: ${link.status.detail}` : ''}`}
        >
          <span className="nd-vpn-label-name">{link.label || 'VPN'}</span>
          <span className="nd-vpn-label-ends">
            {link.a.address ? formatIpv4WithMask(link.a.address) : '?'} ⇄ {link.b.address ? formatIpv4WithMask(link.b.address) : '?'}
          </span>
        </div>
      ))}
    </>
  );
}

/**
 * A site's health is rolled up, so it has no `statusMessage` of its own — the
 * reason a site reads "degraded" is *which* resources under it are unhealthy.
 * Names the offenders (with their own reason where the collector gave one) and
 * counts the rest, so the tooltip stays a tooltip rather than a wall of text.
 */
function rollUpReason(resources: Resource[]): string | null {
  const unhealthy = resources.filter((r) => r.health !== 'up' && r.health !== 'maintenance');
  if (unhealthy.length === 0) return null;
  const ranked = [...unhealthy].sort((a, b) => HEALTH_SEVERITY[b.health] - HEALTH_SEVERITY[a.health]);
  const shown = ranked.slice(0, 3).map((r) => {
    const why = r.statusMessage?.trim();
    return `${r.name} — ${r.health}${why ? `: ${why}` : ''}`;
  });
  const rest = ranked.length - shown.length;
  return [...shown, rest > 0 ? `…and ${rest} more` : null].filter(Boolean).join('\n');
}

function ResourceNode({
  resource,
  icon,
  addresses,
  onOpen,
  selection,
  nodeProps,
}: {
  resource: Resource;
  icon?: string;
  addresses: NodeAddress[];
  onOpen: () => void;
  selection?: NodeSelection;
  /** Position, measuring ref and pointer handlers from the map. */
  nodeProps: NodeProps;
}) {
  const Icon = KIND_ICONS[icon ?? ''] ?? Box;
  const selected = selection?.isSelected(resource.id) ?? false;
  const activate = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (selection && (selection.active || e.ctrlKey || e.metaKey)) selection.toggle(resource.id);
    else onOpen();
  };
  return (
    <div
      className="nd-node nd-node-resource"
      data-clickable="true"
      data-health={resource.health}
      data-selected={selected || undefined}
      role="button"
      tabIndex={0}
      aria-pressed={selection?.active ? selected : undefined}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter') activate(e);
        else if (e.key === ' ' && selection?.active) {
          e.preventDefault();
          selection.toggle(resource.id);
        }
      }}
      {...nodeProps}
    >
      <div className="nd-node-title-row">
        {selection?.active ? (
          selected ? <CheckSquare size={13} className="nd-node-check" /> : <Square size={13} className="nd-node-icon" />
        ) : (
          <Icon size={13} className="nd-node-icon" />
        )}
        <ReachDot health={resource.health} />
        <span className="nd-node-title">{resource.name}</span>
      </div>
      <NodeAddresses primary={accessHostFor(resource)} addresses={addresses} />
      {resource.kind === 'goip-gateway' && <GoipLineCounts resource={resource} />}
      <ResourceHealth resource={resource} />
      <IgnoredBadge resource={resource} />
    </div>
  );
}

/**
 * A GoIP's lines at a glance — in a call, ringing, idle, down — from its last
 * collected sample. Absent until the collector has written the counts, so an
 * older sample or an unreachable gateway shows nothing rather than zeros.
 */
function GoipLineCounts({ resource }: { resource: Resource }) {
  const a = resource.attributes ?? {};
  const n = (key: string): number | null => (typeof a[key] === 'number' ? (a[key] as number) : null);
  const inCall = n('linesInCall');
  const idle = n('linesIdle');
  const down = n('linesDown');
  if (inCall === null || idle === null || down === null) return null;
  const ringing = n('linesRinging') ?? 0;
  const total = n('linesTotal');
  const chips: Array<[string, number, string]> = [
    ['in call', inCall, 'connected'],
    ...(ringing > 0 ? ([['ringing', ringing, 'ringing']] as Array<[string, number, string]>) : []),
    ['idle', idle, 'idle'],
    ['down', down, 'down'],
  ];
  return (
    <div
      className="nd-goip-lines"
      title={`${total ?? inCall + ringing + idle + down} GSM lines: ${inCall} in a call, ${ringing} dialling or ringing, ${idle} idle, ${down} down (off GSM or off the PBX)`}
    >
      {chips.map(([label, count, state]) => (
        <span key={label} className="nd-goip-chip" data-state={state} data-zero={count === 0 || undefined}>
          <span className="nd-goip-dot" />
          {count} {label}
        </span>
      ))}
    </div>
  );
}

/**
 * The node's address lines: the management address first (as before), each
 * with its netmask where one is known, then the node's other interface and
 * tunnel addresses. Capped so a router with twenty VLANs stays a node, not a
 * list — the rest are in the tooltip and on the resource page.
 */
function NodeAddresses({ primary, addresses }: { primary: string | null; addresses: NodeAddress[] }) {
  const MAX = 4;
  const primaryIp = primary?.split('/')[0] ?? null;
  const primaryEntry = addresses.find((a) => a.ip === primaryIp);
  const rest = addresses.filter((a) => a.ip !== primaryIp).sort((a, b) => Number(b.tunnel) - Number(a.tunnel));
  const shown = rest.slice(0, primary ? MAX - 1 : MAX);
  const hidden = rest.length - shown.length;
  const all = [primaryEntry ?? (primaryIp ? { ip: primaryIp, prefix: null, label: 'management', tunnel: false } : null), ...rest]
    .filter((a): a is NodeAddress => a !== null)
    .map((a) => `${a.label ? `${a.label}: ` : ''}${a.ip}${a.prefix === null ? '' : `/${a.prefix}`}`)
    .join('\n');

  return (
    <div className="nd-node-addrs" title={all || undefined}>
      {primary ? (
        primaryEntry ? (
          <IpMask address={primaryEntry} className="nd-node-ip" />
        ) : (
          <span className="nd-node-ip">{primary}</span>
        )
      ) : (
        !shown.length && <span className="nd-node-ip">—</span>
      )}
      {shown.map((a) => (
        <span key={a.ip} className="nd-node-ip" data-tunnel={a.tunnel || undefined}>
          {a.tunnel && <Waypoints size={10} className="nd-node-ip-icon" />}
          <IpMask address={a} />
        </span>
      ))}
      {hidden > 0 && <span className="nd-node-ip subtle">+{hidden} more</span>}
    </div>
  );
}
